import type { ReportPartialFrame } from "~/helpers/reportPartialFrame.helper";

/**
 * Merge a `report_partial` snapshot into an **existing** report-details cache
 * entry, for `queryClient.setQueryData(["reports", "details", id], ...)`.
 *
 * Report content is replaced wholesale (never merged), but the snapshot carries
 * no DB-only fields — DEK envelope, favorite/summary flags — so those survive
 * from the prior fetch. `report_status` is also kept from the cache: the
 * snapshot reuses the SnapApiResponse envelope where it is a bare string
 * ("pending"), while `isPendingReport` requires the dict shape
 * ({status_report, snap_progress}) that the GET and every save persist.
 *
 * Returns `undefined` when there is no entry yet, which TanStack Query treats
 * as "no update". A partial must never seed the cache: `staleTime: Infinity`
 * means the details query would adopt that entry on mount without ever
 * fetching, so a report opened mid-generation would read as "ready" — no
 * pending banner, no progress bar until a reload.
 */
export function mergeReportDetailsPartialSnapshot(
  previous: Record<string, unknown> | undefined,
  snapshot: ReportPartialFrame["data"],
): Record<string, unknown> | undefined {
  if (!previous) return undefined;

  return {
    ...previous,
    ...snapshot,
    report_status: previous.report_status,
  };
}
